import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, AlertCircle, User, Briefcase, Clock, FileText, MessageSquare, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { theme } from '../theme';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  OPEN: { bg: '#FEF3C7', text: '#D97706' },
  IN_REVIEW: { bg: '#DBEAFE', text: '#2563EB' },
  RESOLVED: { bg: '#D1FAE5', text: '#059669' },
  CLOSED: { bg: '#F3F4F6', text: '#6B7280' },
};

export default function CreatorDisputeDetailPage() {
  const { disputeId } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/api/creator/disputes/${disputeId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setDispute(res.data.dispute))
      .catch(() => toast.error('Dispute not found'))
      .finally(() => setLoading(false));
  }, [disputeId]);

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Loading...</div>;
  if (!dispute) return <div style={{ padding: '60px', textAlign: 'center', color: theme.colors.text.secondary }}>Not found</div>;

  const sc = STATUS_COLORS[dispute.status] || STATUS_COLORS.OPEN;

  return (
    <div data-testid="dispute-detail-page" style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
      <button data-testid="dispute-detail-back" onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.primary, fontSize: '14px', fontWeight: 600, marginBottom: '24px' }}>
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <AlertCircle size={24} color={sc.text} />
          <h1 data-testid="dispute-detail-subject" style={{ fontSize: '24px', fontWeight: 700, color: theme.colors.text.primary, margin: 0 }}>{dispute.subject}</h1>
          <span data-testid="dispute-detail-status" style={{ padding: '4px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, background: sc.bg, color: sc.text }}>{dispute.status}</span>
        </div>
        <p style={{ fontSize: '13px', color: theme.colors.text.muted }}>Ticket ID: {dispute.id}</p>
      </div>

      {/* Info Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <User size={14} color={theme.colors.primary} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Holder</span>
          </div>
          <p data-testid="dispute-detail-buyer" style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{dispute.buyer_email}</p>
        </CardContent></Card>

        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <Briefcase size={14} color={theme.colors.status.info} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Portfolio</span>
          </div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary, cursor: 'pointer' }}
            onClick={() => dispute.portfolio_id && navigate(`/creator/portfolios/${dispute.portfolio_id}`)}>
            {dispute.portfolio_name}
          </p>
        </CardContent></Card>

        <Card><CardContent className="pt-4 pb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <FileText size={14} color={theme.colors.status.warning} /><span style={{ fontSize: '12px', color: theme.colors.text.muted }}>Type</span>
          </div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.text.primary }}>{dispute.dispute_type}</p>
        </CardContent></Card>
      </div>

      {/* Full Description */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <MessageSquare size={14} color={theme.colors.primary} />
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Description</p>
        </div>
        <p data-testid="dispute-detail-description" style={{ fontSize: '14px', color: theme.colors.text.secondary, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{dispute.description}</p>
      </CardContent></Card>

      {/* NFT / Transfer IDs if present */}
      {(dispute.nft_id || dispute.transfer_id) && (
        <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary, marginBottom: '8px' }}>Reference IDs</p>
          {dispute.nft_id && <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>NFT ID: <code>{dispute.nft_id}</code></p>}
          {dispute.transfer_id && <p style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Transfer ID: <code>{dispute.transfer_id}</code></p>}
        </CardContent></Card>
      )}

      {/* Admin Notes */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <FileText size={14} color={theme.colors.status.info} />
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Admin Notes</p>
          <span style={{ fontSize: '11px', color: theme.colors.text.muted }}>(read-only)</span>
        </div>
        {dispute.admin_notes ? (
          <p style={{ fontSize: '14px', color: theme.colors.text.secondary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{dispute.admin_notes}</p>
        ) : (
          <p style={{ fontSize: '13px', color: theme.colors.text.muted, fontStyle: 'italic' }}>No admin notes yet.</p>
        )}
      </CardContent></Card>

      {/* Attachments */}
      <Card style={{ marginBottom: '20px' }}><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Paperclip size={14} color={theme.colors.text.muted} />
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Attachments</p>
        </div>
        {dispute.attachments?.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {dispute.attachments.map((a, i) => (
              <a key={i} href={a.url || '#'} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: theme.colors.primary }}>{a.name || `Attachment ${i + 1}`}</a>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: '13px', color: theme.colors.text.muted, fontStyle: 'italic' }}>No attachments.</p>
        )}
      </CardContent></Card>

      {/* Timeline */}
      <Card><CardContent className="pt-5 pb-5">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Clock size={14} color={theme.colors.text.muted} />
          <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Timeline</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderLeft: `2px solid ${theme.colors.borders}`, paddingLeft: '16px' }}>
          <div>
            <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Created</p>
            <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{new Date(dispute.created_at).toLocaleString()}</p>
          </div>
          {dispute.updated_at && dispute.updated_at !== dispute.created_at && (
            <div>
              <p style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.text.primary }}>Last Updated</p>
              <p style={{ fontSize: '12px', color: theme.colors.text.muted }}>{new Date(dispute.updated_at).toLocaleString()}</p>
            </div>
          )}
        </div>
      </CardContent></Card>
    </div>
  );
}
